// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chat-layout.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chat-citation-routing.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/isReviewStatusMessage.test.ts
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chatUtils.test.ts
import {
  type AskPunttMessage,
  type AskPunttProjectData,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowsInSimple,
  ArrowsOutSimple,
  CaretRight,
  CircleNotch,
  ShareNetwork,
  Stop,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import { askPunttChatRoute } from "./chatRoute";
import {
  type AskPunttMessageExt,
  ASK_PUNTT_SKILL_LABELS,
  collectReviewTicketIds,
  connectorsArrayToRecord,
  findContextFileIndex,
  mergeMessageStatus,
} from "./chatUtils";
import { AskPunttDropZone } from "./components/AskPunttDropZone";
import {
  AssetViewerPane,
  type AssetViewerPaneHandle,
} from "./components/AssetViewerPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasPane } from "./components/CanvasPane";
import { ChatMessage } from "./components/ChatMessage";
import { CollapsedPaneLabel } from "./components/CollapsedPaneLabel";
import {
  InputPlusMenu,
  type ConnectorOption,
} from "./components/InputPlusMenu";
import { PasswordProtectedPdfDialog } from "./components/PasswordProtectedPdfDialog";
import { ProjectLoadingPane } from "./components/ProjectLoadingPane";
import { ProjectReviewStatusBanner } from "./components/ProjectReviewStatusBanner";
import { ProjectStatusPane } from "./components/ProjectStatusPane";
import {
  type QuestionFormHandle,
  QuestionForm,
} from "./components/QuestionForm";
import { ResizeHandle } from "./components/ResizeHandle";
import { ResizeHintTooltip } from "./components/ResizeHintTooltip";
import {
  ReviewChecklist,
  TICKET_REVIEW_CHECKS,
} from "./components/ReviewChecklist";
import { ShareAskPunttChatDialog } from "./components/ShareAskPunttChatDialog";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { ThinkingState } from "./components/ThinkingState";
import { TicketViewPane } from "./components/TicketViewPane";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { useFileDrop } from "./hooks/useFileDrop";
import {
  DEFAULT_STALE_THRESHOLD_MS,
  isKillerFlowAssistantMessage,
  useKillerFlowStream,
} from "./hooks/useKillerFlowStream";
import { usePersistedLayout } from "./hooks/usePersistedLayout";
import { useTicketViewData } from "./hooks/useTicketView";
import { useUiPrefs } from "./hooks/useUiPrefs";
import { askPunttRoute } from "./route";

import { PageHeader } from "../../components/PageHeader";
import { getGuidelines } from "../../services/brand-guidelines";
import { editComment } from "../../services/projects";
import { PasswordProtectedPdfError } from "../../services/upload";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import {
  canUseAskPunttConnectors,
  isMeaningfulGigsOrDemo,
} from "../../utils/auth";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import {
  askPunttKeys,
  useAbortAskPunttMessage,
  useAskPunttChatHistory,
  useAskPunttStreamingState,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";
import { setDrawerOpen } from "../../utils/slices/ui";

const STREAM_POLL_MS = 500;
const FULL_POLL_MS = 3000;
const POLL_TIMEOUT_MS = 300_000;

// Upper bound on how long the "review limbo" predicate is allowed to keep
// the refetch poll alive after the most recent ticket-creation assistant
// message landed (see `isReviewInLimbo` for the full rationale). 15 min
// comfortably brackets the single-ticket post-review summary worst case
// (review pipeline + `_run_post_review_summary` agent's 5 min
// `start_to_close_timeout` plus retry headroom) while bounding the
// multi-ticket batch overhead: rembrandt's batch flow can run up to 2
// hours and never writes a follow-up assistant message that flips
// `hasViewTicketIdOnLastAssistant` false, so without a time cap the FE
// would keep polling at FULL_POLL_MS for the entire 2-hour window.
const LIMBO_MAX_MS = 15 * 60 * 1000;

// Per-ticket "review underway" messages: empty content + only thought/text
// agent steps. They look pending but are actually complete server-side events
// emitted by the project review workflow. Exported for regression testing
// (PR #3311 follow-up: stop misclassifying these as streaming on hydration).
export const isReviewStatusMessage = (m: AskPunttMessageExt): boolean =>
  m.role === "assistant" &&
  m.content === "" &&
  (m.agentSteps?.length ?? 0) > 0 &&
  (m.agentSteps ?? []).every(
    (s) =>
      (s as { tool?: string; type?: string }).tool === "thought" ||
      (s as { tool?: string; type?: string }).type === "text",
  );

// Returns true when the last message in a chat is a server-side pending
// assistant turn that the current session didn't create — i.e. the
// post-review auto-summary placeholder. Used to keep the poll alive until
// the summary resolves without the user having to refresh.
export const isPostReviewSummaryPending = (
  messages: AskPunttMessageExt[],
): boolean => {
  const last = messages[messages.length - 1];
  if (!last) return false;
  return (
    last.role === "assistant" &&
    (last.status === "pending" || !last.content) &&
    !isReviewStatusMessage(last)
  );
};

// Decide whether the saved `chat.reviewChecklist` should be treated as the
// final, post-review state (anchored inline at the last review-status
// message) or as in-progress state (rendered as the floating widget below
// the messages).
//
// The naive form — `hasReviewChecklist && !reviewPolling` — misfires during
// the window between the chat workflow saving the initial checklist
// (rembrandt `ask_puntt_workflow.py` step 4b) and the review-trigger child
// workflow setting `chat.reviewStatus` (which is what flips `reviewPolling`
// true). In that window the checklist exists but polling hasn't started,
// so the naive form would call the review "complete" and hide the
// in-progress floating widget that should be showing.
//
// To survive that window we require BOTH (a) we're not currently polling
// AND (b) we have evidence the review actually ran at some point. Evidence
// is either "the component observed a server-side `reviewStatus` at some
// point in its lifetime" (live-session latch) or "the chat already has at
// least one review-status message in its history" (page-reload-after-
// completion). Either is sufficient. The latch must NOT gate on the local
// `reviewPolling` state because that variable is briefly flipped true by
// `setReviewPolling(true)` whenever a new assistant message with a
// `viewTicketId` lands (see `chat.tsx` line ~406) — that flash is an FE
// artifact, not real evidence of the backend review pipeline running.
export const deriveReviewChecklistComplete = (input: {
  hasReviewChecklist: boolean;
  reviewPolling: boolean;
  hasObservedServerReviewStatus: boolean;
  hasReviewStatusMessagesInHistory: boolean;
}): boolean =>
  input.hasReviewChecklist &&
  !input.reviewPolling &&
  (input.hasObservedServerReviewStatus ||
    input.hasReviewStatusMessagesInHistory);

// "Limbo" state: a review has been kicked off (the chat agent created a
// ticket — `viewTicketId` is set on the most recent non-review-status
// assistant message) and `chat.reviewStatus` is currently null. The FE
// must keep polling the chat document during this window — otherwise
// React Query stops polling (because nothing else is in-flight), and we
// never see the backend's eventual `reviewStatus` update or the
// post-review summary message, so the UI silently stays stale.
//
// We key on the assistant message's `viewTicketId` (not on
// `chat.reviewChecklist`) because rembrandt's `ask_puntt_workflow.py`
// writes the assistant response FIRST (step 4), then runs
// `generate_initial_checklist_activity` to populate `reviewChecklist`
// (step 4b, ~45s LLM call), then spawns the review-trigger child workflow
// that finally sets `reviewStatus` (step 5). Keying on `viewTicketId` lets
// us survive the entire window from step-4-saved to step-5-reviewStatus,
// not just the narrower step-4b-to-step-5 sub-window.
//
// IMPORTANT: this predicate also covers the **post-review summary**
// window (bug/final-summary). Sequence:
//
//   1. AskPunttReviewTriggerWorkflow runs the review and clears
//      `reviewStatus` in its `finally`.
//   2. `_run_post_review_summary` then re-sets `reviewStatus =
//      "Generating review summary"` and creates a placeholder message.
//   3. The summary agent runs (up to 5 min), saves the message, and
//      `finally` clears `reviewStatus` again.
//
// Between (1) and (2) the FE sees `reviewStatus = null` but the chat
// already has review-status messages in history from the review run.
// Previously this predicate also required
// `!hasReviewStatusMessagesInHistory`, which made it return `false` here
// — combined with the 120 s stall detector flipping `reviewPolling`
// false during step (2)'s long agent run, polling could stop entirely
// and never restart, even though the summary was being saved to
// MongoDB. The user had to refresh the page to see the final summary.
//
// Dropping the historical-evidence clause is safe because
// `hasViewTicketIdOnLastAssistant` automatically flips false the moment
// a real follow-up assistant message lands (the post-review summary
// itself, which has no `viewTicketId`). It skips review-status messages
// — so the summary placeholder, while empty + thought-only, is also
// skipped, and we keep polling. As soon as the summary content is
// flushed (`status: "completed"`, non-empty content), the summary
// becomes the latest non-review-status assistant, the predicate
// returns false, and polling stops.
//
// 15-MINUTE CAP (`LIMBO_MAX_MS`):
//
// Rembrandt's multi-ticket batch flow (`len(created_ticket_ids) > 1`)
// never writes a follow-up assistant message after the batch finishes
// — it spawns `AskPunttBatchReviewTriggerWorkflow` with
// `ParentClosePolicy.ABANDON` and skips `_run_post_review_summary`
// entirely. Per-phase review-status messages also stay pending forever
// with empty content. So `hasViewTicketIdOnLastAssistant` stays true,
// `serverReviewStatus` stays null after the batch completes, and the
// unbounded predicate would poll at FULL_POLL_MS for the entire
// 2-hour batch execution timeout (plus indefinitely afterward, until
// the user navigates away). Server-side bandwidth regression.
//
// The 15-min cap, anchored on the ticket-creation assistant message's
// `createdAt` timestamp, covers the single-ticket post-review summary
// worst case end-to-end while bounding multi-ticket overhead:
//   - Mid-review on a multi-ticket batch: `serverReviewStatus` is
//     non-null, so `reviewPolling = true` and this predicate isn't
//     consulted at all — the cap never bites here.
//   - After a multi-ticket batch completes (could be 45+ min after the
//     ticket-creation message): the message is older than 15 min →
//     predicate returns false → polling stops cleanly.
//   - Cold-loading a long-completed chat: the ticket-creation message
//     is hours old → predicate returns false on the first poll →
//     polling never starts. Correct.
//
// Callers pass `null` for `viewTicketAssistantAgeMs` when no
// viewTicketId message exists or its `createdAt` is missing — that
// case must return `false` (no limbo).
//
// Exported as a pure helper so the polling decision can be tested without
// instantiating React Query.
export const isReviewInLimbo = (input: {
  hasViewTicketIdOnLastAssistant: boolean;
  serverReviewStatus: string | null | undefined;
  viewTicketAssistantAgeMs: number | null;
}): boolean => {
  if (!input.hasViewTicketIdOnLastAssistant) return false;
  if (input.serverReviewStatus) return false;
  if (input.viewTicketAssistantAgeMs === null) return false;
  return input.viewTicketAssistantAgeMs <= LIMBO_MAX_MS;
};

// Shared scan for the latest non-review-status assistant message,
// iterating from the end and skipping per-phase review-status messages
// (empty content + thought-only `agentSteps`) which never carry
// `viewTicketId` and would mask the real signal during the review
// pipeline's mid-flight status churn.
const findLatestNonReviewStatusAssistant = (
  messages: AskPunttMessageExt[],
): AskPunttMessageExt | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "assistant") continue;
    if (isReviewStatusMessage(m)) continue;
    return m;
  }
  return null;
};

// True when the most recent non-review-status assistant message carries a
// `viewTicketId` — the freshest FE-visible signal that the chat agent
// has just kicked off a review on this turn.
//
// Exported so the polling decision can be tested without instantiating
// React Query.
export const hasViewTicketIdOnLastAssistant = (
  messages: AskPunttMessageExt[],
): boolean => {
  const m = findLatestNonReviewStatusAssistant(messages);
  return !!m?.viewTicketId;
};

// Returns the `createdAt` of the latest non-review-status assistant
// message that carries a `viewTicketId`, or `null` if no such message
// exists. Drives the 15-min `isReviewInLimbo` cap so the predicate can
// stop polling once a multi-ticket batch flow has been idle long
// enough that any post-review summary would already have landed (see
// the cap section of the `isReviewInLimbo` doc above).
//
// Exported for unit testing.
export const getLatestViewTicketAssistantCreatedAt = (
  messages: AskPunttMessageExt[],
): string | null => {
  const m = findLatestNonReviewStatusAssistant(messages);
  if (!m || !m.viewTicketId) return null;
  // Defensive: schema requires `createdAt`, but narrow at runtime in
  // case a malformed payload reaches us — we'd rather return null and
  // skip the time-cap check than throw inside `refetchInterval`.
  return typeof m.createdAt === "string" ? m.createdAt : null;
};

// Whether the single floating ReviewChecklist should render. Pulled out as a
// pure helper so the truth table can be pinned with unit tests without
// instantiating the whole chat component.
//
// The `reviewChecklistAnchorIdx === -1` fallback handles single-ticket
// "general" workflow chats: rembrandt's project review workflow emits
// empty-content "review underway" messages that the inline anchor at the
// last review-status message attaches to, but the single-ticket flow never
// produces those messages. So when `deriveReviewChecklistComplete` flips
// true (e.g. the `hasObservedServerReviewStatusRef` latch fired during a
// mid-flight phase like "Preparing artwork for AI review" and then
// `reviewStatus` dropped back to null between phases or after a failed
// review-trigger child workflow), `reviewChecklistAnchorIdx` stays at -1
// and the inline render never fires. Without this fallback the floating
// widget would also drop out and the user would lose the checklist UI
// entirely even though `chat.reviewChecklist` is populated in MongoDB.
// Keep the floating widget alive so the saved checklist stays visible
// regardless of whether the latch reflects a real completion or a
// mid-flight false positive.
export const shouldShowFloatingReviewChecklist = (input: {
  waitingForAI: boolean;
  lastIsAssistant: boolean;
  /**
   * True when the last message is a still-incomplete assistant message —
   * i.e. `status === "pending"` or content is empty because the agent
   * tool loop hasn't finished writing it yet.
   *
   * Load-bearing for the in-flight review window: in the killer-flow path
   * (any chat where the user asks for a "review"), rembrandt writes a
   * pending assistant placeholder during step 4 (~10-30s for the agent
   * to call `create_ticket`), then continues running for another
   * 60-240s while step 4b populates `chat.reviewChecklist` and step 5
   * spawns the review-trigger child workflow. Throughout that window
   * `waitingForAI` stays true (FE only clears it when a non-pending
   * assistant message lands — see chat.tsx ~538) AND `lastIsAssistant`
   * is true (the placeholder exists). Before this flag was added, the
   * `showWhileWaiting` branch required `!lastIsAssistant`, so the
   * floating widget never rendered during this critical window even
   * though the BE had already signalled review activity (create_ticket
   * fired, then reviewChecklist landed). User-visible symptom: KJ
   * uploaded a file, asked for a review, and saw nothing for 2-4
   * minutes — the test for this case
   * (ask.puntt.review-checklist.spec.ts) hung waiting for the widget.
   */
  lastAssistantIsPending: boolean;
  reviewPolling: boolean;
  hasReviewChecklist: boolean;
  isKillerFlowStreaming: boolean;
  hasCreateTicket: boolean;
  reviewChecklistComplete: boolean;
  reviewChecklistAnchorIdx: number;
}): boolean => {
  const checklistHasNoAnchor = input.reviewChecklistAnchorIdx === -1;
  const completeWithAnchor =
    input.reviewChecklistComplete && !checklistHasNoAnchor;

  // Show after a COMPLETED assistant response when a review is in flight
  // or the saved checklist is already on the chat doc.
  const showAfterResponse =
    !input.waitingForAI &&
    input.lastIsAssistant &&
    (input.reviewPolling ||
      input.hasReviewChecklist ||
      input.isKillerFlowStreaming) &&
    !completeWithAnchor;

  // "While waiting" covers:
  //   (a) the user just sent the message, no assistant message exists
  //       yet (last role === "user"), OR
  //   (b) the agent has written a pending placeholder assistant message
  //       but is still mid-loop (last role === "assistant" with empty/
  //       pending content). This is the dominant in-flight state of a
  //       real review and must NOT block the widget.
  const lastIsUserOrPendingAssistant =
    !input.lastIsAssistant || input.lastAssistantIsPending;

  const showWhileWaiting =
    input.waitingForAI &&
    lastIsUserOrPendingAssistant &&
    (input.reviewPolling ||
      input.hasReviewChecklist ||
      input.hasCreateTicket) &&
    !completeWithAnchor;

  return showAfterResponse || showWhileWaiting;
};

/**
 * How long the citation / pin highlight remains visible on the viewer
 * after the user clicks. Was 3 s; bumped to 5 s after Nestle CSV #11
 * feedback that the ping was hard to spot on busy spec PDFs. Paired
 * with a higher-contrast layered ring in AssetViewerPane (steady ring
 * + animated ping) so the highlight never blinks out between pulses.
 */
const HIGHLIGHT_DURATION_MS = 5000;

export default function AskPunttChat() {
  const { chatId } = askPunttChatRoute.useParams();
  const queryClient = useQueryClient();
  const [waitingForAI, setWaitingForAI] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  // Tracks the most recently pending message ID so agentSteps merge still works
  // after pendingMessageId is cleared (prevents the flash-on-completion bug).
  const lastPendingIdRef = useRef<string | null>(null);
  // True while a background canvas task is still writing — keeps the streaming
  // poll alive after the text response is complete so the full canvas renders.
  const [canvasPolling, setCanvasPolling] = useState(false);

  const [reviewPolling, setReviewPolling] = useState(false);
  // True when the server has a pending assistant message that the current
  // session didn't create — i.e. the post-review summary placeholder appeared
  // in MongoDB while waitingForAI was already false. Stored as state so that
  // updates (via the effect below) trigger a re-render and cause refetchInterval
  // to flip to FULL_POLL_MS for the duration of the summary generation.
  const [serverHasPendingMessage, setServerHasPendingMessage] = useState(false);

  // Full document poll: 1s until we find the pending message, then slow to 3s.
  //
  // `refetchInterval` is a function so it can branch on the latest fetched
  // chat document — specifically, to keep polling alive during the "review
  // limbo" window between the chat agent saving its response with
  // `viewTicketId` (rembrandt `ask_puntt_workflow.py` step 4) and
  // `chat.reviewStatus` being set by the review-trigger child workflow
  // (step 5). Without this, the FE drops to `refetchInterval: false` once
  // the chat agent completes, never sees the backend's eventual
  // `reviewStatus`, and the in-progress floating checklist never appears.
  // See `isReviewInLimbo` above.
  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: (q) => {
      if (waitingForAI) {
        return pendingMessageId ? FULL_POLL_MS : STREAM_POLL_MS;
      }
      if (canvasPolling || reviewPolling || serverHasPendingMessage) {
        return FULL_POLL_MS;
      }
      const data = q.state.data;
      const messages = (data?.messages ?? []) as AskPunttMessageExt[];
      const viewTicketCreatedAt =
        getLatestViewTicketAssistantCreatedAt(messages);
      const viewTicketAssistantAgeMs = viewTicketCreatedAt
        ? Math.max(0, Date.now() - new Date(viewTicketCreatedAt).getTime())
        : null;
      const inLimbo = isReviewInLimbo({
        hasViewTicketIdOnLastAssistant:
          hasViewTicketIdOnLastAssistant(messages),
        serverReviewStatus: data?.chat?.reviewStatus ?? null,
        viewTicketAssistantAgeMs,
      });
      return inLimbo ? FULL_POLL_MS : false;
    },
  });

  // Lightweight streaming poll: only the pending message's streaming fields.
  // Stays active while canvasPolling so background canvas writes reach the UI.
  const streamingMessageId =
    pendingMessageId ?? (canvasPolling ? lastPendingIdRef.current : null);
  const { data: streamingState } = useAskPunttStreamingState(
    chatId,
    streamingMessageId,
    {
      enabled: !!(streamingMessageId && (waitingForAI || canvasPolling)),
      refetchInterval: STREAM_POLL_MS,
    },
  );
  const sendMessage = useSendAskPunttMessage(chatId);
  const abortMessage = useAbortAskPunttMessage(chatId);
  const analytics = useAnalytics("ask_puntt_chat");
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  // --- Instrumentation: time-to-first-response ---
  const messageSentAtRef = useRef<number | null>(null);
  const ttfrFiredRef = useRef(false);

  // --- Instrumentation: session depth ---
  const sessionStartRef = useRef(Date.now());
  const messageCountRef = useRef(0);
  const workflowTypeRef = useRef<string | undefined>(undefined);
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  // Role-gated: OPS doesn't see connectors. See `canUseAskPunttConnectors`
  // in `utils/auth.ts` for the rationale; matches the home.tsx derivation
  // and the `ask.puntt.connectors.spec.ts > Not visible for OPS` contract.
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      canUseAskPunttConnectors()
        ? (guidelinesQuery.data?.askPunttConnectors ?? [])
            .filter((c) => c.enabled && c.connected)
            .map((c) => ({
              id: c.connectorId,
              label: `${c.name} search`,
              icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
            }))
        : [],
    [guidelinesQuery.data?.askPunttConnectors],
  );

  const [inputMessage, setInputMessage] = useState("");
  const [sectionContext, setSectionContext] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [localMessages, setLocalMessages] = useState<AskPunttMessageExt[]>([]);
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
    localPreviewUrls,
  } = useAttachedFiles({
    onUploadError: (err) => {
      if (err instanceof PasswordProtectedPdfError) {
        setProtectedPdfFileName(err.fileName);
      }
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      });
    },
  });
  const [protectedPdfFileName, setProtectedPdfFileName] = useState<
    string | null
  >(null);
  const [passwordPdfViewerWarning, setPasswordPdfViewerWarning] = useState<
    string | null
  >(null);
  const handlePasswordRequired = useCallback((fileName: string) => {
    setPasswordPdfViewerWarning(fileName);
  }, []);
  // Page-wide drag-and-drop is owned by the outer AskPunttDropZone wrapper
  // below. Only retain the clipboard paste handler here so users can still
  // paste screenshots / images directly into the composer.
  const { handlePaste } = useFileDrop({ onDrop: addFiles });
  const [canvasDismissed, setCanvasDismissed] = useState(false);
  const [ticketDismissed, setTicketDismissed] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadRef = useRef(true);
  const knownMessageCountRef = useRef(0);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);
  const prevDataUpdatedAtRef = useRef<number>(0);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
      setPendingMessageId(null);
    }
  }, [waitingForAI]);

  // Clean up highlight ping timer on unmount
  useEffect(() => {
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    };
  }, []);

  // Keep lastPendingIdRef in sync so we can merge agentSteps even after
  // pendingMessageId is cleared to null.
  useEffect(() => {
    if (pendingMessageId) {
      lastPendingIdRef.current = pendingMessageId;
    }
  }, [pendingMessageId]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      const messages = chatData.messages;
      knownMessageCountRef.current = messages.length;
      setLocalMessages(messages);
      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" ||
          (last.role === "assistant" &&
            (last.status === "pending" || !last.content) &&
            !isReviewStatusMessage(last)))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    const dataChanged = dataUpdatedAt !== prevDataUpdatedAtRef.current;
    if (dataChanged) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setLocalMessages((prev) => {
        const mergeId = pendingMessageId ?? lastPendingIdRef.current;
        if (!mergeId) return chatData.messages;
        const localPending = prev.find((m) => m._id === mergeId);
        if (!localPending) return chatData.messages;
        return chatData.messages.map((m) =>
          m._id === mergeId ? mergeMessageStatus(localPending, m) : m,
        );
      });
      knownMessageCountRef.current = chatData.messages.length;
    }

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    if (!dataChanged) return;

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.content &&
      lastMsg.status !== "pending"
    ) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      // Reset canvas/ticket dismissed state when new response arrives
      if (lastMsg.canvasContent) {
        setCanvasDismissed(false);
      }
      if (lastMsg.viewTicketId) {
        setTicketDismissed(false);
        setReviewPolling(true);
      }
    }
    // Detect pending assistant message ID for lightweight polling
    if (waitingForAI && dataChanged && !pendingMessageId) {
      const pendingMsg = chatData.messages.find(
        (m) => m.role === "assistant" && (m.status === "pending" || !m.content),
      );
      if (pendingMsg?._id) {
        setPendingMessageId(pendingMsg._id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  // Detect the pending assistant message ID for lightweight streaming poll
  useEffect(() => {
    if (!waitingForAI || !chatData?.messages || pendingMessageId) return;
    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      (lastMsg.status === "pending" || !lastMsg.content)
    ) {
      setPendingMessageId(lastMsg._id);
    }
  }, [chatData?.messages, waitingForAI, pendingMessageId]);

  // Track whether the server has a pending assistant message we didn't create.
  // Applies when waitingForAI is false — covers the post-review summary
  // placeholder that appears after the review finishes without a user prompt.
  useEffect(() => {
    if (waitingForAI) {
      setServerHasPendingMessage(false);
      return;
    }
    setServerHasPendingMessage(
      isPostReviewSummaryPending(chatData?.messages ?? []),
    );
  }, [chatData?.messages, waitingForAI]);

  // Merge lightweight streaming state into localMessages for faster UI updates
  useEffect(() => {
    if (!streamingState) return;

    // Drive canvasPolling: start when backend signals canvas is generating,
    // stop when canvasLoading clears (canvas complete or errored).
    if (streamingState.canvasLoading && !canvasPolling) {
      setCanvasPolling(true);
    } else if (!streamingState.canvasLoading && canvasPolling) {
      setCanvasPolling(false);
    }

    // After the text response is done, pendingMessageId is null but we still
    // need to apply canvas updates — use lastPendingIdRef for that phase.
    const activeId = pendingMessageId ?? lastPendingIdRef.current;
    if (!activeId) return;

    // Instrumentation: time-to-first-response
    if (
      !ttfrFiredRef.current &&
      messageSentAtRef.current &&
      (streamingState.streamingContent || streamingState.content)
    ) {
      const ttfrMs = Date.now() - messageSentAtRef.current;
      analytics.capture("ask_puntt_time_to_first_response", {
        chat_id: chatId,
        ttfr_ms: ttfrMs,
        workflow_type: chatData?.chat?.workflowType,
      });
      ttfrFiredRef.current = true;
    }

    setLocalMessages((prev) =>
      prev.map((msg) => {
        if (msg._id !== activeId) return msg;
        // Only write fields the streaming payload actually provides.
        // Unconditionally overwriting `canvasContent`/`canvasLoading` with
        // `undefined` was causing the right-pane canvas to unmount and
        // re-mount on every poll tick (visible as a full-pane flicker while
        // the user types follow-up instructions).
        const patch: Partial<AskPunttMessageExt> = {};
        if (streamingState.status !== undefined)
          patch.status = streamingState.status as AskPunttMessage["status"];
        if (streamingState.streamingContent !== undefined)
          patch.streamingContent = streamingState.streamingContent;
        if (streamingState.canvasLoading !== undefined)
          patch.canvasLoading = streamingState.canvasLoading;
        if (streamingState.suggestedResponses !== undefined)
          patch.suggestedResponses = streamingState.suggestedResponses;
        if (streamingState.content) patch.content = streamingState.content;
        if (streamingState.agentSteps?.length)
          patch.agentSteps =
            streamingState.agentSteps as AskPunttMessage["agentSteps"];
        if (streamingState.searchProgress?.length)
          patch.searchProgress =
            streamingState.searchProgress as AskPunttMessage["searchProgress"];
        if (streamingState.canvasContent)
          patch.canvasContent =
            streamingState.canvasContent as AskPunttMessage["canvasContent"];
        if (streamingState.canvasChangedSectionIds?.length)
          patch.canvasChangedSectionIds =
            streamingState.canvasChangedSectionIds;
        if (streamingState.viewTicketId)
          patch.viewTicketId = streamingState.viewTicketId;
        if (streamingState.projectData)
          patch.projectData =
            streamingState.projectData as AskPunttMessage["projectData"];
        if (streamingState.projectLoading !== undefined)
          patch.projectLoading = streamingState.projectLoading;
        if (streamingState.contentBlocks)
          patch.contentBlocks =
            streamingState.contentBlocks as AskPunttMessage["contentBlocks"];
        return { ...msg, ...patch };
      }),
    );
  }, [streamingState, pendingMessageId, canvasPolling]);

  // Keep workflowType ref in sync so the unmount cleanup reads fresh data
  useEffect(() => {
    workflowTypeRef.current = chatData?.chat?.workflowType;
  }, [chatData?.chat?.workflowType]);

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });

    // Instrumentation: session depth on unmount
    return () => {
      const durationMs = Date.now() - sessionStartRef.current;
      analytics.capture("ask_puntt_session_ended", {
        chat_id: chatId,
        duration_ms: durationMs,
        message_count: messageCountRef.current,
        workflow_type: workflowTypeRef.current,
      });
    };
  }, [chatId]);

  // Detect the latest canvas content from messages.
  //
  // The right-pane 2-pane ternary picks ``activeCanvas`` before
  // ``effectiveViewTicketId`` (see ``rightPane2PaneInner`` below),
  // so this memo is load-bearing for "which surface wins the right
  // pane when both a stale canvas and a fresh review exist."
  //
  // Bug guarded against: if the user's most-recent assistant turn is
  // a NEW review (carries ``viewTicketId`` and no ``canvasContent``),
  // we explicitly return ``null`` so the older canvas from a prior
  // turn doesn't keep covering the new review. Without this gate the
  // canvas walks back through history and grabs the first
  // canvas-bearing message it finds, which means: ask for a review →
  // ticket pane → ask for ROI canvas → canvas pane → ask for another
  // review → ticket lands but the canvas stays on top forever.
  //
  // The two other places that pick between canvas and ticket — the
  // collapsed-pane label and the persisted-layout storage key —
  // already prefer the ticket when both exist; this brings the body
  // ternary in line with that intent.
  //
  // We DO NOT clear the canvas when the latest assistant turn is a
  // non-review message (e.g. a plain follow-up question), because in
  // that case the user is still likely consuming the canvas and just
  // having a side conversation about it. The trigger is specifically
  // "a new review landed without bringing its own canvas."
  const activeCanvas = useMemo((): {
    canvas: CanvasContent & { changedSectionIds?: string[] };
    skill?: string;
  } | null => {
    if (canvasDismissed) return null;
    // Latest-turn check: only suppress a stale canvas when the
    // newest completed assistant message is a review (viewTicketId
    // present, no canvas of its own). Pending and user messages are
    // skipped so an in-flight turn doesn't flicker the right pane.
    let suppressForFreshReview = false;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role !== "assistant") continue;
      if (msg.status === "pending" || !msg.content) continue;
      if (
        msg.viewTicketId &&
        !(
          msg.canvasContent &&
          (msg.canvasContent as CanvasContent).blocks?.length > 0
        )
      ) {
        suppressForFreshReview = true;
      }
      break;
    }
    if (suppressForFreshReview) return null;
    // Find the last assistant message with canvasContent
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return {
          canvas: {
            ...(msg.canvasContent as CanvasContent),
            changedSectionIds: msg.canvasChangedSectionIds,
          },
          skill: msg.activeSkill ?? undefined,
        };
      }
    }
    return null;
  }, [localMessages, canvasDismissed]);

  // Instrumentation: canvas rendered (tracked per messageId to avoid duplicate events)
  const canvasTrackedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeCanvas) return;
    // Find the message ID for the current active canvas
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        if (!canvasTrackedRef.current.has(msg._id)) {
          analytics.capture("ask_puntt_canvas_rendered", {
            chat_id: chatId,
            output_type: activeCanvas.canvas.outputType,
            block_count: activeCanvas.canvas.blocks?.length ?? 0,
            skill: activeCanvas.skill,
          });
          canvasTrackedRef.current.add(msg._id);
        }
        break;
      }
    }
  }, [activeCanvas, chatId, localMessages]);

  // Check if any message has canvas content (regardless of dismissed state)
  const hasCanvasContent = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [localMessages]);

  // Detect canvas loading state (agent is generating canvas content).
  // Not gated on waitingForAI — canvas can still be generating after the text
  // response is saved, so we rely on canvasPolling for the post-text phase.
  const isCanvasLoading = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(
      lastMsg?.role === "assistant" &&
      (lastMsg.canvasLoading || canvasPolling)
    );
  }, [localMessages, canvasPolling]);

  // Detect project loading state (agent is generating project data)
  const isProjectLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.projectLoading);
  }, [localMessages, waitingForAI]);

  // Collect all viewTicketIds in chronological order (de-duplicated). Drives
  // the "Review N of M" navigation in the right pane so users can revisit
  // earlier reviews when multiple sequential reviews exist in one chat.
  const allReviewTicketIds = useMemo(
    () => collectReviewTicketIds(localMessages),
    [localMessages],
  );

  const hasViewTicketId = allReviewTicketIds.length > 0;

  const [selectedReviewIndex, setSelectedReviewIndex] = useState(-1);

  // Sticky auto-advance: jump to the newest review only when the user is
  // already on the latest one (or has no selection yet). Preserves manual
  // navigation when the user has gone back to an earlier review.
  useEffect(() => {
    if (allReviewTicketIds.length === 0) return;
    setSelectedReviewIndex((prev) => {
      if (prev < 0 || prev === allReviewTicketIds.length - 1) {
        return allReviewTicketIds.length - 1;
      }
      return prev;
    });
  }, [allReviewTicketIds.length]);

  const activeViewTicketId = useMemo((): string | null => {
    if (ticketDismissed) return null;
    if (
      selectedReviewIndex >= 0 &&
      selectedReviewIndex < allReviewTicketIds.length
    ) {
      return allReviewTicketIds[selectedReviewIndex];
    }
    return null;
  }, [ticketDismissed, selectedReviewIndex, allReviewTicketIds]);

  const handlePrevReview = useCallback(() => {
    setSelectedReviewIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNextReview = useCallback(() => {
    setSelectedReviewIndex((i) =>
      Math.min(allReviewTicketIds.length - 1, i + 1),
    );
  }, [allReviewTicketIds.length]);

  // LLM-provided pane labels for 3-panel layout (e.g. "Brief" / "Deliverable")
  const activePaneLabels = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.paneLabels) return msg.paneLabels;
    }
    return undefined;
  }, [localMessages]);

  // Theatrical typewriter reveal for the Vitalora killer-flow demo
  // response. The hook self-gates by inspecting the latest assistant
  // message's shape (`isKillerFlowAssistantMessage`), so we only
  // need a coarse tenant-level enable flag here — production tenants
  // never land a killer-flow-shaped payload, so the hook is a no-op
  // for them in practice. We keep this gated on MG/demo as a safety
  // net so an accidental killer-flow payload on a real tenant doesn't
  // surprise anyone with a 90s typewriter.
  //
  // Declared here (rather than its historical position after every
  // useEffect) so `leftPaneProjectData` below can read
  // `isKillerFlowStreaming` as one of its in-flight signals — killer-
  // flow chats never set `chat.reviewStatus`, so without this the
  // synthesized stub would never fire for single-asset killer-flow
  // chats whose backend hasn't yet been updated to emit `projectData`.
  const killerStream = useKillerFlowStream({
    enabled: isMeaningfulGigsOrDemo(),
    messages: localMessages,
  });
  const isKillerFlowStreaming = killerStream.isStreaming;

  // Detect project data from chat-level or latest message
  const activeProjectData = useMemo((): AskPunttProjectData | null => {
    const chatLevel = chatData?.chat?.projectData as
      | AskPunttProjectData
      | null
      | undefined;
    if (chatLevel) return chatLevel;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.projectData) {
        return msg.projectData as AskPunttProjectData;
      }
    }
    return null;
  }, [chatData?.chat?.projectData, localMessages]);

  // Fallback `projectData` synthesized for the LEFT pane only.
  //
  // Ronnie's demo flow requires every in-flight review chat (single,
  // multi, custom) to look identical in the chat pane: yellow "AI
  // review in progress" banner above the composer + parallel
  // `ReviewChecklist` with one row per asset per check. The flows
  // each have a different way of signalling "a review is running"
  // and only one of them emits `projectData`:
  //
  //   * Multi-asset killer-flow      → backend emits `projectData`
  //   * Single-asset killer-flow     → backend emits `projectData`
  //                                    (post-unification, commit 68ff878bd)
  //                                    BUT christo deploys lag the FE,
  //                                    so we also accept
  //                                    `isKillerFlowStreaming` as an
  //                                    in-flight signal and synthesize
  //                                    from chat-level / message-level
  //                                    ticket ids.
  //   * Real-flow review (rembrandt) → no `projectData` ever; the
  //                                    workflow sets `chat.reviewStatus`
  //                                    instead. We synthesize from
  //                                    that signal + ticket ids.
  //
  // The stub only drives the BANNER and the CHECKLIST. `activeProjectData`
  // stays backend-only so the right-pane decision tree (which has
  // different requirements per flow — image preview for single-asset,
  // status cards for multi-asset) is unaffected by synthesis.
  //
  // Once the in-flight signal clears — `reviewStatus` cleared by the
  // workflow OR the typewriter finishes — this returns null and the
  // yellow banner disappears.
  const chatLevelTicketIds = (
    chatData?.chat as { ticketIds?: string[] } | undefined
  )?.ticketIds;
  const chatLevelReviewStatus = chatData?.chat?.reviewStatus;

  const leftPaneProjectData = useMemo((): AskPunttProjectData | null => {
    if (activeProjectData) return activeProjectData;
    const inFlight = !!chatLevelReviewStatus || isKillerFlowStreaming;
    if (!inFlight) return null;
    // Collect every ticket id we have any signal for, in priority
    // order: chat-level (set on persistence by the killer-flow path)
    // → assistant-message viewTicketId (set when a review completes)
    // → `Create Ticket` search-progress events (set the moment the
    // ticket exists, before the review finishes).
    const ids = new Set<string>();
    for (const id of chatLevelTicketIds ?? []) ids.add(id);
    for (const msg of localMessages) {
      if (msg.role === "assistant" && msg.viewTicketId) {
        ids.add(msg.viewTicketId);
      }
      for (const sp of msg.searchProgress ?? []) {
        if (sp.source === "Create Ticket") {
          for (const r of (sp.results ?? []) as {
            id?: string;
            title?: string;
          }[]) {
            if (r.id) ids.add(r.id);
          }
        }
      }
    }
    if (ids.size === 0) return null;
    // Single "active" stage is what `ProjectReviewStatusBanner` looks
    // for to render the yellow "AI review in progress" pill. We can't
    // know the real stage taxonomy for a custom asset (it depends on
    // which reviewers the enterprise has configured), so we ship one
    // generic "AI review" stage rather than fabricating reviewer-
    // specific stages that would mislead the buyer once findings land.
    return {
      ticketIds: Array.from(ids),
      stages: [{ name: "AI review", status: "active", color: "amber" }],
    } as AskPunttProjectData;
  }, [
    activeProjectData,
    chatLevelReviewStatus,
    chatLevelTicketIds,
    isKillerFlowStreaming,
    localMessages,
  ]);

  // For project chats: pool the canonical review checks from each ticket so
  // the ReviewChecklist rotates through items across all parallel reviews.
  // Items are interleaved (round-robin) across tickets so reveals feel
  // parallel rather than ticket-by-ticket. Driven by `leftPaneProjectData`
  // (backend-emitted OR synthesized stub) so single-asset and custom-asset
  // reviews show the same per-ticket rows as multi-asset killer-flow.
  const projectChecklistItems = useMemo(() => {
    if (!leftPaneProjectData) return undefined;
    if (chatData?.chat?.reviewChecklist) return undefined;
    const ticketIds = leftPaneProjectData.ticketIds ?? [];
    if (ticketIds.length === 0) return undefined;

    const titleMap = new Map<string, string>();
    for (const msg of localMessages) {
      for (const sp of msg.searchProgress ?? []) {
        if (sp.source === "Create Ticket") {
          for (const r of (sp.results ?? []) as {
            id?: string;
            title?: string;
          }[]) {
            if (r.id && r.title) titleMap.set(r.id, r.title);
          }
        }
      }
    }

    const checks = TICKET_REVIEW_CHECKS;
    const interleaved: { label: string; sub: string; ticketLabel: string }[] =
      [];
    for (let checkIdx = 0; checkIdx < checks.length; checkIdx++) {
      for (const ticketId of ticketIds) {
        const ticketLabel = titleMap.get(ticketId) ?? "Ticket";
        interleaved.push({
          ticketLabel,
          label: checks[checkIdx].label,
          sub: checks[checkIdx].sub,
        });
      }
    }
    return interleaved;
  }, [leftPaneProjectData, localMessages, chatData?.chat?.reviewChecklist]);

  // Parallel-mode threshold is `>= 1` (not `> 1`) so single-asset and
  // custom-asset reviews share the multi-asset look. The checklist's
  // header line gracefully handles count=1 ("Reviewing your asset")
  // and count>1 ("Reviewing N assets in parallel").
  const projectTicketCount = leftPaneProjectData?.ticketIds?.length ?? 0;
  const projectParallelTicketCount =
    projectTicketCount >= 1 ? projectTicketCount : 0;

  // Track which ticket the user drilled into from the project view
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // When the user drills into a project ticket, use that ID for data fetching.
  // For project chats, only show a ticket when explicitly selected (drill-in);
  // otherwise the project overview is the default landing view. Single-ticket
  // projects auto-drill-in once the review completes — see the useEffect
  // below that watches `isKillerFlowStreaming` and the synthesized
  // `leftPaneProjectData`. The right pane therefore stays on
  // `ProjectStatusPane` during the in-flight review (single card with
  // "Reviewing…" pill, matching Ronnie's spec) and flips to
  // `TicketViewPane` (image + pins + comments) the moment the reveal /
  // workflow finishes.
  const effectiveViewTicketId = activeProjectData
    ? selectedTicketId
    : activeViewTicketId;

  // Lift ticket data to parent to determine layout mode
  const {
    comments: viewComments,
    isLoading: viewTicketLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(effectiveViewTicketId, {
    pollingEnabled: reviewPolling,
  });

  // Auto-select the first comment when comments arrive and none is selected
  useEffect(() => {
    if (!selectedCommentId && viewComments?.length) {
      setSelectedCommentId(viewComments[0]._id ?? null);
    }
  }, [selectedCommentId, viewComments]);

  // Stable reference for the "no comments" case so AssetViewerPane's memo
  // doesn't break on every render of this route. Without this, `comments
  // ?? []` produces a fresh array each render → memo always misses →
  // pdfjs / tldraw / image-canvas children re-render every drag tick.
  const stableViewComments = useMemo(() => viewComments ?? [], [viewComments]);

  const isProjectChat = chatData?.chat?.workflowType === "project";

  // Progressive reveal contract for the Vitalora killer-flow demo:
  // the right-hand `ProjectStatusPane` now mounts immediately when
  // `activeProjectData` lands so the buyer sees ticket cards from
  // t=0 (with a per-card "Reviewing…" badge). Each card flips to
  // its finished state — severity badge, risk chip, comment count —
  // when the killer-flow typewriter finishes. The banner above the
  // composer is similarly suppressed-to-"AI review in progress"
  // during streaming so the red "needs human review" call-out
  // doesn't appear before reviewers have visibly run.
  const displayProjectData = activeProjectData;

  const {
    scrollContainerRef,
    sentinelRef: messagesEndRef,
    contentRef: messagesContentRef,
    handleScroll: handleMessagesScroll,
    resetScroll: resetAutoScroll,
  } = useAutoScroll(
    [
      localMessages,
      waitingForAI,
      streamingState,
      // Killer-flow typewriter tick signals — the typewriter overlays
      // content on a message whose stored ref doesn't change per frame,
      // so without these the hook would only fire once and the chat
      // would freeze at the top while text streams in below the fold.
      killerStream.activeMessageId,
      killerStream.revealedContentBlocks,
      killerStream.revealedAgentStepCount,
      killerStream.isStreaming,
    ],
    // resetKey — switching to a new chat clears the sticky-up state so
    // the layout effect re-pins to the bottom of the new chat instead
    // of inheriting the previous chat's scroll position.
    chatId,
  );

  // Enable 3-pane (brief + artwork + comments) when viewing a ticket with
  // context assets. In project chats this fires only when the user has
  // drilled into a specific ticket (effectiveViewTicketId falls back to
  // selectedTicketId when activeProjectData is set, see above), so the
  // project overview remains the default landing view.
  const use3PaneLayout = !!effectiveViewTicketId && hasContextAssets;

  const has2PaneRight =
    !use3PaneLayout &&
    !!(
      effectiveViewTicketId ||
      activeCanvas ||
      isProjectLoading ||
      activeProjectData
    );

  // Reset selected ticket when navigating to a different chat
  useEffect(() => {
    setSelectedTicketId(null);
  }, [chatId]);

  // Reset selected comment when switching between project tickets
  useEffect(() => {
    setSelectedCommentId(null);
  }, [selectedTicketId]);

  // Auto-drill-in for single-ticket projects once the review reveal is
  // complete. Ronnie's flow: a single-asset Ask Puntt chat shows the
  // multi-asset right-pane look (`ProjectStatusPane` with one card and
  // a "Reviewing…" pill) WHILE the killer-flow typewriter is running,
  // then flips to `TicketViewPane` (image + pins + comments) the
  // moment the reveal finishes. Multi-ticket projects don't auto-
  // drill-in because the buyer needs the overview to scan all the
  // assets at once.
  //
  // Race-guard: when a fresh killer-flow response lands,
  // `activeProjectData` flips truthy on render N but
  // `useKillerFlowStream`'s `setActive(...)` doesn't flush its state
  // update until render N+1. Without `killerStreamingObservedRef` the
  // effect fires on render N seeing `isKillerFlowStreaming === false`
  // (stale), sets selectedTicketId, and the user lands on
  // `TicketViewPane` BEFORE the typewriter has had a chance to start.
  // The ref latches when streaming is first observed; when the latest
  // assistant message is a fresh killer-flow shape we hold off until
  // we've seen streaming actually fire (or until the message is older
  // than the staleness window, at which point the streaming hook
  // marked it handled and won't run — history-replay path).
  const killerStreamingObservedRef = useRef(false);
  useEffect(() => {
    if (isKillerFlowStreaming) {
      killerStreamingObservedRef.current = true;
    }
  }, [isKillerFlowStreaming]);

  useEffect(() => {
    if (selectedTicketId) return;
    if (!activeProjectData) return;
    const ticketIds = activeProjectData.ticketIds ?? [];
    if (ticketIds.length !== 1) return;
    if (isKillerFlowStreaming) return;
    const latest = localMessages[localMessages.length - 1];
    if (latest?.role === "assistant" && isKillerFlowAssistantMessage(latest)) {
      const createdAtMs = latest.createdAt ? Date.parse(latest.createdAt) : NaN;
      const isFresh =
        !Number.isNaN(createdAtMs) &&
        Date.now() - createdAtMs < DEFAULT_STALE_THRESHOLD_MS;
      if (isFresh && !killerStreamingObservedRef.current) return;
    }
    setSelectedTicketId(ticketIds[0]);
  }, [
    activeProjectData,
    isKillerFlowStreaming,
    selectedTicketId,
    localMessages,
  ]);

  const [labelSheetCollapsed, setLabelSheetCollapsed] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [artworkCollapsed, setArtworkCollapsed] = useState(false);

  // When entering 3-column layout, collapse the app sidebar (if open) to free horizontal space
  const prev3Pane = useRef(false);
  useEffect(() => {
    if (use3PaneLayout && !prev3Pane.current && sidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prev3Pane.current = use3PaneLayout;
  }, [use3PaneLayout, sidebarOpen, dispatch]);

  const handleDismissCanvas = useCallback(() => {
    setCanvasDismissed(true);
  }, []);

  const handleSectionClick = useCallback((sectionId: string, title: string) => {
    setSectionContext({ id: sectionId, title });
  }, []);

  const handleProjectDataUpdate = useCallback(
    (updatedData: AskPunttProjectData) => {
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (old: typeof chatData) => {
          if (!old?.chat) return old;
          return {
            ...old,
            chat: { ...old.chat, projectData: updatedData },
          };
        },
      );
    },
    [chatId, queryClient],
  );

  const labelSheetRef = useRef<AssetViewerPaneHandle>(null);
  const artworkPaneRef = useRef<AssetViewerPaneHandle>(null);

  // ---- Resizable panes ----
  // The resize feature activates only when the container is >= 1024 px AND
  // every visible pane is uncollapsed. iPad portrait (768 px) and any
  // collapsed state fall back to the legacy flex layout so the existing
  // 32 px expand bars + 300 ms transitions keep working unchanged. There is
  // no feature flag — once this ships it's the default behavior; safety net
  // is the width + collapse-state gates, not PostHog.
  const paneContainerRef = useRef<HTMLDivElement | null>(null);
  const containerWidth = useContainerWidth(paneContainerRef);
  const widthOk = containerWidth === null ? false : containerWidth >= 1024;
  const { prefs: uiPrefs, setPref: setUiPref } = useUiPrefs();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const panelGroup2PaneRef = useRef<ImperativePanelGroupHandle>(null);
  const panelGroup3PaneRef = useRef<ImperativePanelGroupHandle>(null);

  // ---- Resize layout derivation ----
  // Resize is only "eligible" when every visible pane is expanded. Once any
  // pane collapses we fall back to the legacy flex layout so the existing
  // 32 px expand bars + collapse transitions keep working unchanged.
  const allPanesExpanded2 = !chatCollapsed && !artworkCollapsed;
  const allPanesExpanded3 =
    !chatCollapsed && !labelSheetCollapsed && !artworkCollapsed;
  const resizeBaseEligible = widthOk;
  const useResizable2Pane =
    resizeBaseEligible && has2PaneRight && allPanesExpanded2;
  const useResizable3Pane =
    resizeBaseEligible && use3PaneLayout && allPanesExpanded3;
  const useResizableLayout = useResizable2Pane || useResizable3Pane;

  // Storage key per visible right-pane variant so a chat+canvas 60/40
  // doesn't bleed into a chat+ticket session.
  const twoPaneStorageKey = effectiveViewTicketId
    ? "askpuntt-2pane-chat-ticket"
    : activeCanvas
      ? "askpuntt-2pane-chat-canvas"
      : "askpuntt-2pane-chat-project";

  const TWO_PANE_DEFAULT: number[] = [50, 50];
  const TWO_PANE_MINS: number[] = [25, 30]; // ≈320px / ≈360px @ 1280px
  const THREE_PANE_DEFAULT: number[] = [25, 50, 25];
  const THREE_PANE_MINS: number[] = [22, 22, 22]; // ≈280px each @ 1280px

  const twoPaneLayout = usePersistedLayout({
    storageKey: twoPaneStorageKey,
    defaultLayout: TWO_PANE_DEFAULT,
    minSizes: TWO_PANE_MINS,
  });
  const threePaneLayout = usePersistedLayout({
    storageKey: "askpuntt-3pane",
    defaultLayout: THREE_PANE_DEFAULT,
    minSizes: THREE_PANE_MINS,
  });

  // Abort signal for ResizeHandle — when chatId or effectiveViewTicketId
  // changes mid-drag, the handle synthesizes a pointerup so cursor +
  // userSelect body styles don't get stuck.
  const dragAbortSignal = `${chatId ?? ""}:${effectiveViewTicketId ?? ""}`;

  // Suppress the existing 300ms flex-grow transitions on the layout subtree
  // while a handle is being dragged. Without this the pane animations
  // fight the resize. Re-enabled the moment drag ends.
  const dragInProgress = activeDragId !== null;

  const questionFormRef = useRef<QuestionFormHandle>(null);
  const [_questionHasAnswer, setQuestionHasAnswer] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionHasAnswer, setCurrentQuestionHasAnswer] =
    useState(false);
  const questionUploadInputRef = useRef<HTMLInputElement>(null);
  const [contextHighlight, setContextHighlight] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const contextHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleCitationClick = useCallback(
    (
      pageNumber: number,
      position?: { x: number; y: number },
      sourceUrl?: string,
      originalURL?: string,
    ) => {
      // Switch context pane to the file the citation points to. Signed URLs
      // carry ephemeral query-string signatures, so match on pathname only,
      // with originalURL as a fallback for files that carry an unsigned key.
      const targetIdx = findContextFileIndex(
        contextFiles,
        sourceUrl,
        originalURL,
      );
      if (targetIdx >= 0) {
        // Pass pageNumber along so it lands on the newly-mounted viewer even
        // when citations have no rel_x/rel_y to drive the highlight effect.
        labelSheetRef.current?.navigateToFile(targetIdx, pageNumber);
      } else {
        labelSheetRef.current?.navigateToPage(pageNumber);
      }
      if (position) {
        if (contextHighlightTimerRef.current)
          clearTimeout(contextHighlightTimerRef.current);
        // Clear first to restart the animation when clicking the same source again
        setContextHighlight(null);
        requestAnimationFrame(() => {
          setContextHighlight({
            x: position.x,
            y: position.y,
            page: pageNumber,
          });
          contextHighlightTimerRef.current = setTimeout(
            () => setContextHighlight(null),
            HIGHLIGHT_DURATION_MS,
          );
        });
      }
    },
    [contextFiles],
  );

  useEffect(() => {
    return () => {
      if (contextHighlightTimerRef.current) {
        clearTimeout(contextHighlightTimerRef.current);
      }
    };
  }, []);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      setSelectedCommentId(comment._id ?? null);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(
          () => setHighlightPing(null),
          HIGHLIGHT_DURATION_MS,
        );
      }
    },
    [],
  );

  const pinDragMutation = useMutation({ mutationFn: editComment });

  const handlePinDragEnd = useCallback(
    (comment: BaseTicketComment, newRelX: number, newRelY: number) => {
      if (!activeViewTicketId || !comment._id) return;

      // Optimistic update: immediately reflect new position in cache
      queryClient.setQueryData<BaseTicketComment[]>(
        ["ticket-view-comments", activeViewTicketId],
        (old) =>
          old?.map((c) =>
            c._id === comment._id
              ? { ...c, rel_x: newRelX, rel_y: newRelY }
              : c,
          ),
      );

      pinDragMutation.mutate(
        {
          ticketId: activeViewTicketId,
          messageId: comment._id,
          payload: {
            description: comment.description ?? "",
            rel_x: newRelX,
            rel_y: newRelY,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["ticket-view-comments", activeViewTicketId],
            });
          },
          onError: () => {
            // Revert on failure
            queryClient.invalidateQueries({
              queryKey: ["ticket-view-comments", activeViewTicketId],
            });
          },
        },
      );
    },
    [activeViewTicketId, pinDragMutation, queryClient],
  );

  const handleStop = async () => {
    const msgId = pendingMessageId;
    // Optimistic: re-enable input immediately
    setWaitingForAI(false);
    setPendingMessageId(null);

    if (!msgId) return;

    try {
      await abortMessage.mutateAsync(msgId);
    } catch {
      // Revert: restore polling so the message can complete naturally
      setWaitingForAI(true);
      setPendingMessageId(msgId);
    }
  };

  const handleSend = async (
    overrideMessage?: string,
    extraAttachments?: {
      key: string;
      source: string;
      type: "image" | "video" | "file";
      name?: string;
    }[],
  ) => {
    const text = overrideMessage ?? inputMessage;
    const allAttachments = [...readyAttachments, ...(extraAttachments ?? [])];
    if (
      (!text.trim() && allAttachments.length === 0) ||
      sendMessage.isPending ||
      hasPending
    )
      return;
    resetAutoScroll();

    const messageToSend = text;
    const attachments = allAttachments;
    const messageLength = messageToSend.length;
    const messageIndex = localMessages.filter((m) => m.role === "user").length;

    const userMsg: AskPunttMessage = {
      _id: `local-user-${Date.now()}`,
      chatId,
      role: "user",
      content: messageToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };

    const contextToSend = sectionContext;

    setLocalMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    setSectionContext(null);
    clearAll();

    // Instrumentation: mark send time for TTFR measurement
    messageSentAtRef.current = Date.now();
    ttfrFiredRef.current = false;

    try {
      const result = await sendMessage.mutateAsync({
        message: messageToSend,
        attachments: attachments.length > 0 ? attachments : undefined,
        sectionContext: contextToSend ?? undefined,
      });

      messageCountRef.current += 1;

      // Replace the optimistic user message with the server-confirmed one
      setLocalMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
        return [...withoutOptimistic, result.userMessage];
      });
      knownMessageCountRef.current = localMessages.length;
      dataAtWaitStartRef.current = dataUpdatedAt;
      lastPendingIdRef.current = null;
      setWaitingForAI(true);

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_sent", {
        chat_id: chatId,
        message_length: messageLength,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        message_index: messageIndex,
        workflow_id: result.workflowId,
      });
    } catch (error) {
      setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_send_error", {
        chat_id: chatId,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to send message:", error);
    }
  };

  const chat = chatData?.chat;
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  // Owner-only affordances (Share button, Delete, etc.) — keyed off the
  // chat's `userId` field. `chat.role` is set by christo to "owner" or
  // "shared" but we double-check by comparing ids so the FE keeps working
  // even if the role field is ever omitted.
  const isChatOwner =
    !!chat &&
    !!currentUserId &&
    chat.userId === currentUserId &&
    chat.role !== "shared";
  const allowListMembers = chat?.allowList ?? [];
  const serverReviewStatus = chat?.reviewStatus ?? null;

  // For project chats, the overall review is still pending until findingsSummary
  // arrives — serverReviewStatus is cleared per-ticket so it oscillates to null
  // between parallel ticket reviews. The killer-flow demo backs its `review_started`
  // phase with the typewriter window, so treat that as pending too so the
  // "Review complete" transition toast fires when the reveal finishes.
  const projectReviewPending =
    !!activeProjectData &&
    (activeProjectData.ticketIds?.length ?? 0) > 0 &&
    (!activeProjectData.findingsSummary || isKillerFlowStreaming);

  // Show a transient "Review complete" when the review finishes. For project
  // chats, "reviewing" stays true until findingsSummary arrives, so the toast
  // fires once at the end instead of on every per-ticket transition.
  const isReviewing = !!serverReviewStatus || projectReviewPending;
  const [completionStatus, setCompletionStatus] = useState<string | null>(null);
  const wasReviewingRef = useRef(false);
  useEffect(() => {
    const wasReviewing = wasReviewingRef.current;
    wasReviewingRef.current = isReviewing;
    if (wasReviewing && !isReviewing) {
      setCompletionStatus("Review complete");
      const timer = setTimeout(() => setCompletionStatus(null), 5000);
      return () => clearTimeout(timer);
    }
    if (isReviewing) {
      setCompletionStatus(null);
    }
  }, [isReviewing]);

  const reviewStatus = serverReviewStatus ?? completionStatus;

  // Sync review polling state with reviewStatus from server.
  //
  // Frontend safety-net for the "stuck reviewStatus" class of bug. Rembrandt's
  // post-review summary workflow used to leak `reviewStatus` on partial
  // failure paths (placeholder/context/agent errors would early-return
  // without clearing the field), leaving the ReviewChecklist banner stuck on
  // "Review in progress · Results on ticket" even after every per-phase
  // checkmark went green and the assistant's summary message landed. The
  // root cause is fixed in `_run_post_review_summary` (try/finally so the
  // clear runs unconditionally), but historical stuck chats need a way out
  // and we don't want a future regression of the same shape to hang the UI
  // again.
  //
  // Stall-detector: if `serverReviewStatus` has held a single non-null
  // value for more than `REVIEW_POLL_STALL_MS` AND we've already received
  // an assistant message after the latest user turn, treat the review as
  // effectively done and stop polling locally. Anything *actively*
  // progressing on the backend re-stamps `reviewStatus` with a new phase
  // string and resets the stall timer, so this can never short-circuit a
  // genuinely in-flight review.
  const stallStartRef = useRef<{ status: string; t: number } | null>(null);
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!serverReviewStatus) {
      stallStartRef.current = null;
      setStalled(false);
      return;
    }
    // The post-review summary agent legitimately holds a single static
    // status string ("Generating review summary") for up to 5 min
    // (`start_to_close_timeout=timedelta(minutes=5)` on the agent
    // activity in `services/rembrandt/temporal/workflows/ask_puntt_workflow.py`).
    // Exempting it from the 120 s stall safety net is required because
    // otherwise the detector flips `stalled = true` partway through,
    // `effectiveReviewActive` and `reviewPolling` fall to false, the
    // placeholder summary message already carries thought-only agent
    // steps so `isReviewStatusMessage` classifies it as a review-status
    // message → `isPostReviewSummaryPending` returns false, the new
    // limbo predicate's `!serverReviewStatus` clause also fails (status
    // is non-null), and polling stops mid-summary. The other statuses
    // ("Saving review comments", "Reviewing artwork", phase strings) are
    // meant to refresh every few seconds during the review pipeline, so
    // they remain stall-eligible — a 120 s unchanged value there is
    // genuinely stuck. stall-exempt: see ask_puntt_workflow.py:159
    if (serverReviewStatus === "Generating review summary") {
      stallStartRef.current = null;
      setStalled(false);
      return;
    }
    const prev = stallStartRef.current;
    if (!prev || prev.status !== serverReviewStatus) {
      stallStartRef.current = { status: serverReviewStatus, t: Date.now() };
      setStalled(false);
      return;
    }
    const REVIEW_POLL_STALL_MS = 120_000;
    const elapsed = Date.now() - prev.t;
    if (elapsed >= REVIEW_POLL_STALL_MS) {
      setStalled(true);
      return;
    }
    const timer = setTimeout(
      () => setStalled(true),
      REVIEW_POLL_STALL_MS - elapsed,
    );
    return () => clearTimeout(timer);
  }, [serverReviewStatus]);

  useEffect(() => {
    // Treat a stalled reviewStatus as "not polling" once an assistant
    // message has arrived after the most recent user turn — the visible
    // signal that the review actually finished.
    const lastUserIdx = (() => {
      for (let i = localMessages.length - 1; i >= 0; i--) {
        if (localMessages[i].role === "user") return i;
      }
      return -1;
    })();
    const assistantSinceUser =
      lastUserIdx >= 0 &&
      localMessages
        .slice(lastUserIdx + 1)
        .some((m) => m.role === "assistant" && m.status !== "pending");
    const effectiveReviewActive =
      (!!serverReviewStatus && !(stalled && assistantSinceUser)) ||
      projectReviewPending;
    setReviewPolling(effectiveReviewActive);
  }, [serverReviewStatus, projectReviewPending, stalled, localMessages]);

  // When review polling stops, force a final fetch to pick up any comments
  // saved in the window between the last poll and polling stopping. Also
  // re-invalidate the chat so the frontend catches a new reviewStatus that
  // the backend may have set for a post-review summary (the gap between the
  // review clearing reviewStatus and the summary setting it is ~50ms; the
  // invalidation fires a refetch that catches any new status in that window).
  const prevReviewPollingRef = useRef(false);
  useEffect(() => {
    if (prevReviewPollingRef.current && !reviewPolling && activeViewTicketId) {
      queryClient.invalidateQueries({
        queryKey: ["ticket-view", activeViewTicketId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ticket-view-comments", activeViewTicketId],
      });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
    }
    prevReviewPollingRef.current = reviewPolling;
  }, [reviewPolling, activeViewTicketId, queryClient, chatId]);

  // Find the active skill label from the last assistant message
  const activeSkillLabel = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.activeSkill) {
        return ASK_PUNTT_SKILL_LABELS[msg.activeSkill];
      }
    }
    return undefined;
  }, [localMessages]);

  // Extract suggested responses from the last assistant message
  const activeSuggestedResponses = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.suggestedResponses &&
      lastMsg.suggestedResponses.length > 0
    ) {
      return lastMsg.suggestedResponses;
    }
    return null;
  }, [localMessages]);

  // Hide suggestion pills when the last message has userQuestions (the form handles it)
  const lastMessage = localMessages[localMessages.length - 1];
  const hasQuestionForm =
    lastMessage?.role === "assistant" &&
    lastMessage.userQuestions &&
    lastMessage.userQuestions.length > 0;

  // Reset answer state when a new question form appears
  useEffect(() => {
    if (hasQuestionForm) {
      setQuestionHasAnswer(false);
      setCurrentQuestionHasAnswer(false);
      setCurrentQuestionIndex(0);
    }
  }, [hasQuestionForm]);

  const totalQuestions = hasQuestionForm
    ? (lastMessage?.userQuestions?.length ?? 0)
    : 0;

  const handleQuestionNext = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setCurrentQuestionHasAnswer(false);
    } else {
      questionFormRef.current?.submit();
    }
  }, [currentQuestionIndex, totalQuestions]);

  const handleQuestionBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  }, [currentQuestionIndex]);

  const handleUploadRequest = useCallback(() => {
    questionUploadInputRef.current?.click();
  }, []);
  const showSuggestions =
    activeSuggestedResponses &&
    !waitingForAI &&
    !sendMessage.isPending &&
    !hasQuestionForm;

  // When the review is complete, find the last review-status message so the
  // ReviewChecklist can be anchored inline at that position rather than always
  // floating to the bottom as new messages arrive.
  //
  // Latch: `hasObservedServerReviewStatusRef` flips to true the first time we
  // see a non-null `chat.reviewStatus` from the server and stays true for the
  // rest of the component's lifetime. We gate on `serverReviewStatus` (the
  // authoritative backend signal) rather than the local `reviewPolling` state
  // because `reviewPolling` is briefly flipped true by `setReviewPolling(true)`
  // at line ~406 whenever a fresh assistant message with a `viewTicketId`
  // lands — an FE artifact that does not mean the backend review pipeline is
  // actually running. If we latched on that flash we would wrongly conclude
  // the review had run, return `reviewChecklistComplete = true` during the
  // limbo window, and hide the floating checklist (the original regression).
  //
  // Combined with the `hasReviewStatusMessagesInHistory` fallback for
  // page-reload-after-completion, the latch correctly distinguishes "review
  // genuinely finished" from "review hasn't started yet."
  const hasObservedServerReviewStatusRef = useRef(false);
  useEffect(() => {
    if (serverReviewStatus) hasObservedServerReviewStatusRef.current = true;
  }, [serverReviewStatus]);
  const hasReviewStatusMessagesInHistory = useMemo(
    () => localMessages.some(isReviewStatusMessage),
    [localMessages],
  );
  const reviewChecklistComplete = deriveReviewChecklistComplete({
    hasReviewChecklist: !!chatData?.chat?.reviewChecklist,
    reviewPolling,
    hasObservedServerReviewStatus: hasObservedServerReviewStatusRef.current,
    hasReviewStatusMessagesInHistory,
  });
  const reviewChecklistAnchorIdx = reviewChecklistComplete
    ? localMessages.reduce(
        (last, m, i) => (isReviewStatusMessage(m) ? i : last),
        -1,
      )
    : -1;

  // ---- Hoisted inner JSX for the chat / context / artwork / 2-pane right
  // panes. Each is referenced from BOTH the legacy flex layout AND the new
  // PanelGroup branches so we keep the source-of-truth in one place. The
  // wrappers (which differ between layouts) are inlined at the call sites.
  const chatPaneInner = (
    <>
      {(use3PaneLayout || has2PaneRight) && !chatCollapsed && (
        <div className="flex h-10 shrink-0 items-center justify-end border-b border-puntt-neutral-gray-6 px-4">
          <Tooltip content="Collapse chat pane">
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              aria-label="Collapse chat pane"
              data-testid="pane-collapse-chat"
              onClick={() => setChatCollapsed(true)}
            >
              <ArrowsInSimple size={14} />
            </IconButton>
          </Tooltip>
        </div>
      )}
      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-y-auto px-4 py-6"
        data-testid="ask-puntt-messages"
      >
        <div
          ref={messagesContentRef}
          className="mx-auto max-w-[720px] space-y-4"
        >
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                />
              ))}
            </div>
          ) : (
            <>
              {localMessages.map((message, idx) => {
                const isLast = idx === localMessages.length - 1;
                const isLastAssistant =
                  message.role === "assistant" && isLast && !waitingForAI;
                const precedingUserHasAttachments =
                  isLast &&
                  message.role === "assistant" &&
                  waitingForAI &&
                  !!localMessages[idx - 1]?.attachments?.length;

                // Hide per-ticket "review underway" messages when the
                // ReviewChecklist is active so the checklist is the
                // single source of review progress. The post-map
                // ReviewChecklist replaces the last one. Don't hide a
                // mid-stream message that happens to match the shape.
                const isReviewStatusMsg = isReviewStatusMessage(message);
                const reviewChecklistActive =
                  reviewPolling || !!chatData?.chat?.reviewChecklist;
                if (
                  isReviewStatusMsg &&
                  reviewChecklistActive &&
                  (!isLast || !waitingForAI)
                ) {
                  // Inline anchor: once the review is complete, render the
                  // ReviewChecklist at the last review-status message's
                  // position so it stays fixed in the chat as new messages
                  // arrive below it.
                  if (idx === reviewChecklistAnchorIdx) {
                    return (
                      <ReviewChecklist
                        key={message._id}
                        workflowType={chatData?.chat?.workflowType ?? ""}
                        isReviewPolling={false}
                        reviewComplete={true}
                        dynamicItems={
                          chatData?.chat?.reviewChecklist ??
                          projectChecklistItems
                        }
                        parallelTicketCount={undefined}
                      />
                    );
                  }
                  return null;
                }

                // Theatrical typewriter override for the killer flow:
                // when this is the message currently being paced,
                // swap its `contentBlocks` and slice its `agentSteps`
                // so ChatMessage renders the partial reveal. Once
                // streaming finishes, `revealedContentBlocks` is null
                // and the message renders normally.
                const isStreamingTarget =
                  killerStream.activeMessageId === message._id &&
                  killerStream.revealedContentBlocks !== null;
                const displayedMessage = isStreamingTarget
                  ? {
                      ...message,
                      contentBlocks:
                        killerStream.revealedContentBlocks ?? undefined,
                      agentSteps:
                        killerStream.revealedAgentStepCount != null
                          ? (message.agentSteps ?? []).slice(
                              0,
                              killerStream.revealedAgentStepCount,
                            )
                          : message.agentSteps,
                    }
                  : message;

                return (
                  <ChatMessage
                    key={message._id}
                    message={displayedMessage}
                    chatId={chatId}
                    reviewFiles={latestReviewFiles}
                    localPreviewUrls={localPreviewUrls}
                    isStreaming={
                      (waitingForAI &&
                        isLast &&
                        message.role === "assistant") ||
                      isStreamingTarget
                    }
                    isLastAssistant={isLastAssistant && !isStreamingTarget}
                    onSubmitAnswers={handleSend}
                    formDisabled={sendMessage.isPending || waitingForAI}
                    questionFormRef={
                      isLast && hasQuestionForm ? questionFormRef : undefined
                    }
                    onAnswerChange={
                      isLast && hasQuestionForm
                        ? setQuestionHasAnswer
                        : undefined
                    }
                    hideInlineQuestionForm={!!(isLast && hasQuestionForm)}
                    precedingUserHasAttachments={precedingUserHasAttachments}
                  />
                );
              })}
              {/* Single, stable-position ReviewChecklist render. Lives
                  outside the messages map so its internal stagger/timer
                  state survives new review-status messages arriving from
                  the project review workflow. */}
              {(() => {
                const lastMsg = localMessages[localMessages.length - 1];
                const lastIsAssistant = lastMsg?.role === "assistant";
                // "Pending" covers both the explicit `status: "pending"`
                // flag rembrandt sets on the placeholder + the
                // empty-content state during the agent tool loop (chat
                // agent buffers tokens; content only fills in after the
                // loop completes). See `shouldShowFloatingReviewChecklist`
                // doc for why this is load-bearing during a real review.
                const lastAssistantIsPending =
                  lastIsAssistant &&
                  !!lastMsg &&
                  (lastMsg.status === "pending" || !lastMsg.content);
                const hasReviewChecklist = !!chatData?.chat?.reviewChecklist;
                const hasCreateTicket = localMessages.some((m) =>
                  m.agentSteps?.some(
                    (s) => s.type === "tool_call" && s.tool === "create_ticket",
                  ),
                );

                // Anchor-fallback: when `reviewChecklistComplete` is true
                // but no review-status message exists in history to
                // anchor against, `reviewChecklistAnchorIdx` is -1 and
                // the inline render at line ~1761 never fires. Without
                // this fallback the floating widget would also drop out
                // and the user would lose the checklist UI entirely
                // even though `chat.reviewChecklist` is populated. See
                // `shouldShowFloatingReviewChecklist` doc at chat.tsx
                // top for the full failure mode.
                const showFloating = shouldShowFloatingReviewChecklist({
                  waitingForAI,
                  lastIsAssistant,
                  lastAssistantIsPending,
                  reviewPolling,
                  hasReviewChecklist,
                  isKillerFlowStreaming,
                  hasCreateTicket,
                  reviewChecklistComplete,
                  reviewChecklistAnchorIdx,
                });

                if (showFloating) {
                  return (
                    <ReviewChecklist
                      workflowType={chatData?.chat?.workflowType ?? ""}
                      isReviewPolling={reviewPolling}
                      reviewComplete={reviewChecklistComplete}
                      dynamicItems={
                        chatData?.chat?.reviewChecklist ?? projectChecklistItems
                      }
                      parallelTicketCount={
                        hasReviewChecklist
                          ? undefined
                          : projectParallelTicketCount || undefined
                      }
                    />
                  );
                }

                if (waitingForAI && !lastIsAssistant) {
                  return (
                    <ThinkingState
                      precedingUserHasAttachments={
                        !!localMessages[localMessages.length - 1]?.attachments
                          ?.length
                      }
                    />
                  );
                }

                return null;
              })()}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="shrink-0 border-t border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4">
        <div className="mx-auto max-w-[720px] space-y-3">
          {leftPaneProjectData && (
            <ProjectReviewStatusBanner
              projectData={leftPaneProjectData}
              isKillerFlowStreaming={isKillerFlowStreaming}
            />
          )}
          <div
            className="relative overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20"
            data-testid="ask-puntt-chat-composer"
          >
            <input
              ref={questionUploadInputRef}
              data-testid="ask-puntt-file-upload-input"
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(Array.from(e.target.files));
                e.target.value = "";
              }}
            />

            {hasQuestionForm && !waitingForAI && (
              <div className="border-b border-puntt-neutral-gray-6 px-4 pb-2 pt-3">
                <Flex align="center" justify="between">
                  <Text size="1" className="text-puntt-neutral-gray-9">
                    Question {currentQuestionIndex + 1} of {totalQuestions}
                  </Text>
                  <Flex gap="1">
                    {Array.from({ length: totalQuestions }).map((_, i) => (
                      <div
                        key={i}
                        className={`size-1.5 rounded-full transition-colors duration-200 ${
                          i <= currentQuestionIndex
                            ? "bg-puntt-accent-9"
                            : "bg-puntt-neutral-gray-5"
                        }`}
                      />
                    ))}
                  </Flex>
                </Flex>
              </div>
            )}

            {showSuggestions && (
              <SuggestedResponses
                suggestions={activeSuggestedResponses}
                onSelect={(suggestion) => {
                  analytics.capture("ask_puntt_suggested_response_clicked", {
                    chat_id: chatId,
                    suggestion_text: suggestion.slice(0, 100),
                    workflow_type: chatData?.chat?.workflowType,
                  });
                  handleSend(suggestion);
                }}
                disabled={sendMessage.isPending || waitingForAI}
              />
            )}

            {/* Section context chip */}
            {sectionContext && (
              <div className="flex animate-chip-slide-down items-center gap-2 overflow-hidden border-b border-puntt-accent-4 bg-puntt-accent-2 px-3 py-1.5 text-xs text-puntt-accent-11">
                <span className="flex-1 truncate">
                  Re: <strong>{sectionContext.title}</strong>
                </span>
                <button
                  data-testid="chat-button-1472"
                  onClick={() => setSectionContext(null)}
                  aria-label="Remove section context"
                  className="shrink-0 cursor-pointer rounded p-0.5 hover:bg-puntt-accent-3 focus:outline-none focus:ring-1 focus:ring-puntt-accent-9"
                >
                  ×
                </button>
              </div>
            )}
            {hasQuestionForm && !waitingForAI ? (
              <div className="px-3 py-2">
                <QuestionForm
                  data-testid="chat-question-form-1483"
                  ref={questionFormRef}
                  questions={
                    (lastMessage?.userQuestions as AskPunttUserQuestion[]) ?? []
                  }
                  onSubmit={(formatted, attachments) => {
                    analytics.capture("ask_puntt_question_form_submitted", {
                      chat_id: chatId,
                      question_count: totalQuestions,
                      workflow_type: chatData?.chat?.workflowType,
                    });
                    handleSend(formatted || undefined, attachments);
                  }}
                  disabled={sendMessage.isPending || waitingForAI || hasPending}
                  hideSubmitButton
                  visibleQuestionIndex={currentQuestionIndex}
                  onCurrentQuestionAnswerChange={setCurrentQuestionHasAnswer}
                  onUploadRequest={handleUploadRequest}
                  chatLevelFileCount={attachedItems.length}
                />
              </div>
            ) : (
              <TextArea
                data-testid="ask-puntt-chat-reply-input"
                size="2"
                variant="surface"
                placeholder="Reply..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onPaste={handlePaste}
                className="min-h-[60px] w-full resize-none border-none !shadow-none"
                style={{ outline: "none", boxShadow: "none" }}
              />
            )}

            {/* Attached files preview */}
            {attachedItems.length > 0 && (
              <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                {attachedItems.map((item, idx) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                    data-testid="ask-puntt-attached-file"
                  >
                    {item.status === "pending" && (
                      <CircleNotch
                        size={12}
                        weight="bold"
                        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                      />
                    )}
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "error" && " (failed)"}
                    </Text>
                    <button
                      data-testid="chat-button-1554"
                      onClick={() => removeFile(idx)}
                      className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                      aria-label={`Remove ${item.file.name}`}
                    >
                      ×
                    </button>
                  </Flex>
                ))}
              </Flex>
            )}

            <Flex justify="between" align="center" className="px-3 py-1.5">
              {hasQuestionForm && !waitingForAI ? (
                <>
                  <Button
                    data-testid="chat-button-1569"
                    size="2"
                    variant="ghost"
                    color="gray"
                    disabled={sendMessage.isPending || waitingForAI}
                    onClick={() => questionFormRef.current?.submitAny()}
                    className="cursor-pointer"
                  >
                    Just Get Started
                    <ArrowRight size={14} weight="bold" />
                  </Button>
                  <Flex gap="2" align="center">
                    <Button
                      data-testid="chat-button-1581"
                      size="2"
                      variant="soft"
                      color="gray"
                      disabled={
                        currentQuestionIndex === 0 ||
                        sendMessage.isPending ||
                        waitingForAI
                      }
                      onClick={handleQuestionBack}
                      className="cursor-pointer"
                    >
                      <ArrowLeft size={14} weight="bold" />
                      Back
                    </Button>
                    <Button
                      data-testid="question-next-btn"
                      size="2"
                      variant="solid"
                      color="blue"
                      disabled={
                        !currentQuestionHasAnswer ||
                        sendMessage.isPending ||
                        waitingForAI ||
                        hasPending
                      }
                      onClick={handleQuestionNext}
                      className="cursor-pointer"
                    >
                      {currentQuestionIndex < totalQuestions - 1
                        ? "Next"
                        : "Submit Answers"}
                      <ArrowRight size={14} weight="bold" />
                    </Button>
                  </Flex>
                </>
              ) : (
                <>
                  <InputPlusMenu
                    onFilesSelected={addFiles}
                    onScreenshotCaptured={addFile}
                    enabledConnectors={connectorsArrayToRecord(
                      chat?.enabledConnectors,
                      availableConnectors,
                    )}
                    connectorsDisabled
                    availableConnectors={availableConnectors}
                  />
                  {waitingForAI ? (
                    <Button
                      data-testid="ask-puntt-chat-stop"
                      size="2"
                      variant="soft"
                      color="gray"
                      onClick={handleStop}
                      disabled={abortMessage.isPending}
                      className="cursor-pointer transition-all duration-200"
                      aria-label="Stop generating"
                    >
                      <Stop size={14} weight="fill" />
                      Stop
                    </Button>
                  ) : (
                    <Button
                      data-testid="ask-puntt-chat-send"
                      size="2"
                      variant={
                        inputMessage.trim() || readyAttachments.length > 0
                          ? "solid"
                          : "ghost"
                      }
                      color={
                        hasPending
                          ? "gray"
                          : inputMessage.trim() || readyAttachments.length > 0
                            ? "blue"
                            : "gray"
                      }
                      disabled={
                        (!inputMessage.trim() &&
                          readyAttachments.length === 0) ||
                        sendMessage.isPending ||
                        hasPending
                      }
                      onClick={() => handleSend()}
                      className="cursor-pointer transition-all duration-200"
                      aria-label="Send message"
                    >
                      {hasPending ? (
                        <CircleNotch
                          size={14}
                          weight="bold"
                          className="animate-spin"
                        />
                      ) : (
                        <ArrowUp size={14} weight="bold" />
                      )}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </div>
        </div>
      </div>
    </>
  );

  const contextPaneInner3Pane = viewTicketLoading ? (
    <div className="flex h-full items-center justify-center bg-base-white">
      <Text size="2" className="text-puntt-neutral-gray-9">
        Loading...
      </Text>
    </div>
  ) : (
    <AssetViewerPane
      // Reset internal viewer state (incl. Office snapshot) per
      // ticket so an xlsx → image transition on one ticket can't
      // bleed into the next.
      key={effectiveViewTicketId ?? "no-ticket"}
      ref={labelSheetRef}
      title={activePaneLabels?.context ?? "Context"}
      assets={contextFiles}
      highlightPosition={contextHighlight}
      onCollapse={() => setLabelSheetCollapsed(true)}
      onPasswordRequired={handlePasswordRequired}
    />
  );

  const artworkPaneInner3Pane = viewTicketLoading ? (
    <div className="flex h-full items-center justify-center bg-base-white">
      <Text size="2" className="text-puntt-neutral-gray-9">
        Loading...
      </Text>
    </div>
  ) : (
    <AssetViewerPane
      // Reset internal viewer state (incl. Office snapshot) per
      // ticket so an xlsx → image transition on one ticket can't
      // bleed into the next.
      key={effectiveViewTicketId ?? "no-ticket"}
      ref={artworkPaneRef}
      title={activePaneLabels?.artwork ?? "Artwork"}
      assets={latestReviewFiles}
      reviewStatus={reviewStatus}
      comments={stableViewComments}
      activeCommentId={selectedCommentId}
      onCommentClick={handleCommentClick}
      onPinClick={handleCommentClick}
      onPinDragEnd={handlePinDragEnd}
      onCitationClick={handleCitationClick}
      onCollapse={() => setArtworkCollapsed(true)}
      highlightPosition={highlightPing}
      ticketId={effectiveViewTicketId ?? undefined}
      onBackToProject={
        selectedTicketId && activeProjectData
          ? () => setSelectedTicketId(null)
          : undefined
      }
      onPasswordRequired={handlePasswordRequired}
    />
  );

  const rightPane2PaneInner =
    activeProjectData && selectedTicketId ? (
      <TicketViewPane
        ticketId={selectedTicketId}
        onCollapse={() => setArtworkCollapsed(true)}
        onBackToProject={
          // Single-ticket projects have no meaningful
          // "overview" to return to — one card on a bare
          // pane. Hiding the back affordance also keeps
          // the auto-drill-in effect from fighting a user
          // who accidentally hits Back (the effect would
          // immediately re-select the lone ticket and the
          // UI would jitter).
          (activeProjectData.ticketIds?.length ?? 0) > 1
            ? () => setSelectedTicketId(null)
            : undefined
        }
        reviewStatus={reviewStatus}
        onCitationClick={handleCitationClick}
        onPinDragEnd={handlePinDragEnd}
        isKillerFlowStreaming={isKillerFlowStreaming}
      />
    ) : isProjectChat && isProjectLoading && !activeProjectData ? (
      <ProjectLoadingPane />
    ) : activeCanvas ? (
      <div className="relative h-full" data-testid="ask-puntt-canvas-area">
        <CanvasPane
          canvas={activeCanvas.canvas}
          onDismiss={handleDismissCanvas}
          skillName={
            activeCanvas.skill
              ? ASK_PUNTT_SKILL_LABELS[activeCanvas.skill]
              : undefined
          }
          chatId={chatId}
          isLoading={isCanvasLoading}
          onSectionClick={handleSectionClick}
        />
      </div>
    ) : displayProjectData ? (
      <ProjectStatusPane
        projectData={displayProjectData}
        activeTicketId={effectiveViewTicketId}
        ticketIds={chatData?.chat?.ticketIds ?? []}
        onTicketSelect={setSelectedTicketId}
        chatId={chatId}
        chatCreatedAt={chatData?.chat?.createdAt}
        onSendMessage={(msg) => handleSend(msg)}
        onProjectDataUpdate={handleProjectDataUpdate}
        actionsDisabled={sendMessage.isPending || waitingForAI}
        isLoading={isProjectLoading}
        isKillerFlowStreaming={isKillerFlowStreaming}
      />
    ) : effectiveViewTicketId ? (
      <TicketViewPane
        ticketId={effectiveViewTicketId}
        onCollapse={() => setArtworkCollapsed(true)}
        reviewStatus={reviewStatus}
        onCitationClick={handleCitationClick}
        onPinDragEnd={handlePinDragEnd}
        reviewIndex={selectedReviewIndex}
        totalReviews={allReviewTicketIds.length}
        onPrevReview={handlePrevReview}
        onNextReview={handleNextReview}
        isKillerFlowStreaming={isKillerFlowStreaming}
      />
    ) : null;

  return (
    <AskPunttDropZone
      onFilesDropped={addFiles}
      className="relative flex h-full flex-col overflow-hidden"
      data-testid="ask-puntt-chat"
    >
      {passwordPdfViewerWarning && (
        <div
          className="absolute right-4 top-4 z-[70] max-w-sm rounded-lg border border-puntt-gold-7 bg-base-white p-4 shadow-lg"
          data-testid="password-pdf-warning-toast"
          aria-label="Password-protected PDF warning"
          role="status"
        >
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            Password protected PDF
          </Text>
          <Text as="p" size="1" className="mt-1 text-puntt-neutral-gray-11">
            {passwordPdfViewerWarning} is encrypted. Download it and open with
            your PDF viewer to enter the password.
          </Text>
          <Flex justify="end" mt="2">
            <Button
              data-testid="password-pdf-warning-dismiss-button"
              size="1"
              variant="soft"
              aria-label="Dismiss password warning"
              onClick={() => setPasswordPdfViewerWarning(null)}
              className="cursor-pointer"
            >
              Dismiss
            </Button>
          </Flex>
        </div>
      )}
      {/* Breadcrumb */}
      <PageHeader>
        <Link to={askPunttRoute.to} className="puntt-link">
          <Text
            size="2"
            weight="medium"
            className="text-puntt-accent-11 hover:underline"
          >
            Ask Puntt
          </Text>
        </Link>
        {chat?.title && (
          <>
            <CaretRight size={12} className="text-puntt-neutral-gray-9" />
            <Text
              size="2"
              weight="medium"
              className="max-w-[400px] truncate text-puntt-neutral-gray-11"
            >
              {chat.title}
            </Text>
          </>
        )}
        {chat?.ownerName && (
          <Badge
            size="1"
            variant="soft"
            color="gray"
            className="ml-2"
            data-testid="ask-puntt-chat-owner-badge"
          >
            owned by {chat.ownerName}
          </Badge>
        )}
        {activeSkillLabel && (
          <Badge size="1" variant="soft" color="blue" className="ml-2">
            {activeSkillLabel}
          </Badge>
        )}
        {isChatOwner && (
          <Button
            data-testid="ask-puntt-share-chat-button"
            aria-label="Share chat"
            size="1"
            variant="soft"
            color="gray"
            className="ml-auto cursor-pointer"
            onClick={() => setShareDialogOpen(true)}
          >
            <ShareNetwork size={14} aria-hidden="true" />
            <Text size="1">Share</Text>
          </Button>
        )}
        {ticketDismissed && hasViewTicketId && (
          <Button
            data-testid="chat-button-1170"
            size="1"
            variant="soft"
            color="blue"
            className={
              isChatOwner ? "cursor-pointer" : "ml-auto cursor-pointer"
            }
            onClick={() => setTicketDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Review</Text>
          </Button>
        )}
        {canvasDismissed && hasCanvasContent && (
          <Button
            data-testid="chat-button-1182"
            size="1"
            variant="soft"
            color="blue"
            className={`cursor-pointer ${!(ticketDismissed && hasViewTicketId) && !isChatOwner ? "ml-auto" : ""}`}
            onClick={() => setCanvasDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Canvas</Text>
          </Button>
        )}
      </PageHeader>

      {chat && (
        <ShareAskPunttChatDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          chatId={chat._id}
          chatTitle={chat.title}
          members={allowListMembers}
        />
      )}

      {/* Main content: multi-pane layout */}
      <div
        ref={paneContainerRef}
        className="relative flex flex-1 overflow-hidden"
      >
        {useResizable3Pane ? (
          <PanelGroup
            direction="horizontal"
            ref={panelGroup3PaneRef}
            onLayout={threePaneLayout.onLayout}
            // Intentionally no autoSaveId: persistence is owned by
            // usePersistedLayout (250 ms debounce + validateLayout clamp).
            // Setting autoSaveId in addition would let the library bypass
            // the clamp on subsequent visits and double-write to a parallel
            // localStorage key.
            className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
          >
            <Panel
              id="chat"
              order={0}
              minSize={THREE_PANE_MINS[0]}
              defaultSize={threePaneLayout.layout[0]}
            >
              <div
                className="relative flex h-full flex-col overflow-hidden border-r border-puntt-neutral-gray-6"
                data-testid="pane-chat"
              >
                {chatPaneInner}
              </div>
            </Panel>
            <ResizeHandle
              id="chat-right"
              panelGroupRef={panelGroup3PaneRef}
              defaultLayout={THREE_PANE_DEFAULT}
              abortSignal={dragAbortSignal}
              onDraggingChange={(d) => setActiveDragId(d ? "chat-right" : null)}
            />
            <Panel
              id="context"
              order={1}
              minSize={THREE_PANE_MINS[1]}
              defaultSize={threePaneLayout.layout[1]}
            >
              <div
                className="h-full animate-canvas-slide-in border-r border-puntt-neutral-gray-6"
                data-testid="pane-context"
              >
                {contextPaneInner3Pane}
              </div>
            </Panel>
            <ResizeHandle
              id="context-artwork"
              panelGroupRef={panelGroup3PaneRef}
              defaultLayout={THREE_PANE_DEFAULT}
              abortSignal={dragAbortSignal}
              onDraggingChange={(d) =>
                setActiveDragId(d ? "context-artwork" : null)
              }
            />
            <Panel
              id="artwork"
              order={2}
              minSize={THREE_PANE_MINS[2]}
              defaultSize={threePaneLayout.layout[2]}
            >
              <div
                className="h-full animate-canvas-slide-in"
                data-testid="pane-artwork"
              >
                {artworkPaneInner3Pane}
              </div>
            </Panel>
          </PanelGroup>
        ) : useResizable2Pane ? (
          <PanelGroup
            direction="horizontal"
            ref={panelGroup2PaneRef}
            onLayout={twoPaneLayout.onLayout}
            // See note on the 3-pane PanelGroup above: persistence is owned
            // by usePersistedLayout, not the library's autoSaveId.
            className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
          >
            <Panel
              id="chat"
              order={0}
              minSize={TWO_PANE_MINS[0]}
              defaultSize={twoPaneLayout.layout[0]}
            >
              <div
                className="relative flex h-full flex-col overflow-hidden border-r border-puntt-neutral-gray-6"
                data-testid="pane-chat"
              >
                {chatPaneInner}
              </div>
            </Panel>
            <ResizeHandle
              id="chat-right"
              panelGroupRef={panelGroup2PaneRef}
              defaultLayout={TWO_PANE_DEFAULT}
              abortSignal={dragAbortSignal}
              onDraggingChange={(d) => setActiveDragId(d ? "chat-right" : null)}
            />
            <Panel
              id="right"
              order={1}
              minSize={TWO_PANE_MINS[1]}
              defaultSize={twoPaneLayout.layout[1]}
            >
              <div
                className="h-full animate-canvas-slide-in"
                data-testid="pane-right"
              >
                {rightPane2PaneInner}
              </div>
            </Panel>
          </PanelGroup>
        ) : (
          <>
            {/* Chat Pane */}
            <div
              style={
                has2PaneRight || use3PaneLayout
                  ? {
                      flexGrow: chatCollapsed ? 0 : 1,
                      flexShrink: 1,
                      flexBasis: "0%",
                      minWidth: 0,
                      transition:
                        "flex-grow 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)",
                    }
                  : undefined
              }
              className={`relative flex flex-col overflow-hidden ${
                !(has2PaneRight || use3PaneLayout)
                  ? "w-full"
                  : chatCollapsed
                    ? "pointer-events-none opacity-0"
                    : "border-r border-puntt-neutral-gray-6 opacity-100"
              }`}
            >
              {chatPaneInner}
            </div>

            {/* Right Pane(s) */}
            {use3PaneLayout ? (
              <>
                <CollapsedPaneLabel
                  collapsed={chatCollapsed}
                  onExpand={() => setChatCollapsed(false)}
                  ariaLabel="Expand Chat pane"
                  testId="pane-expand-chat"
                  labelRender={() => "Chat"}
                />

                <CollapsedPaneLabel
                  collapsed={labelSheetCollapsed}
                  onExpand={() => setLabelSheetCollapsed(false)}
                  ariaLabel="Expand Context pane"
                  testId="pane-expand-context"
                  labelRender={() => (
                    <>
                      {activePaneLabels?.context ?? "Context"} &middot;{" "}
                      {contextFiles.length}{" "}
                      {contextFiles.length === 1 ? "file" : "files"}
                    </>
                  )}
                />

                {/* Context pane */}
                <div
                  data-testid="pane-context"
                  style={{
                    flexGrow: labelSheetCollapsed ? 0 : 1,
                    flexShrink: 1,
                    flexBasis: "0%",
                    minWidth: 0,
                    overflow: "hidden",
                    transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                  }}
                  className={`animate-canvas-slide-in ${
                    labelSheetCollapsed
                      ? ""
                      : "border-r border-puntt-neutral-gray-6"
                  }`}
                >
                  <div
                    className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                      labelSheetCollapsed
                        ? "translate-x-5 opacity-0"
                        : "translate-x-0 opacity-100"
                    }`}
                  >
                    {contextPaneInner3Pane}
                  </div>
                </div>

                <CollapsedPaneLabel
                  collapsed={artworkCollapsed}
                  onExpand={() => setArtworkCollapsed(false)}
                  ariaLabel="Expand Artwork pane"
                  testId="pane-expand-artwork"
                  labelRender={() => (
                    <>
                      {activePaneLabels?.artwork ?? "Artwork"} &middot;{" "}
                      {latestReviewFiles.length}{" "}
                      {latestReviewFiles.length === 1 ? "file" : "files"}
                    </>
                  )}
                />

                {/* Artwork pane */}
                <div
                  data-testid="pane-artwork"
                  style={{
                    flexGrow: artworkCollapsed ? 0 : 1,
                    flexShrink: 1,
                    flexBasis: "0%",
                    minWidth: 0,
                    overflow: "hidden",
                    transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                  }}
                  className="animate-canvas-slide-in"
                >
                  <div
                    className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                      artworkCollapsed
                        ? "translate-x-5 opacity-0"
                        : "translate-x-0 opacity-100"
                    }`}
                  >
                    {artworkPaneInner3Pane}
                  </div>
                </div>
              </>
            ) : has2PaneRight ? (
              <>
                <CollapsedPaneLabel
                  collapsed={chatCollapsed}
                  onExpand={() => setChatCollapsed(false)}
                  ariaLabel="Expand Chat pane"
                  testId="pane-expand-chat"
                  labelRender={() => "Chat"}
                />

                <CollapsedPaneLabel
                  collapsed={artworkCollapsed}
                  onExpand={() => setArtworkCollapsed(false)}
                  ariaLabel="Expand pane"
                  testId="pane-expand-artwork"
                  labelRender={() =>
                    effectiveViewTicketId
                      ? "Ticket"
                      : activeCanvas
                        ? "Canvas"
                        : "Details"
                  }
                />

                {/* Right pane content (2-pane) */}
                <div
                  style={{
                    flexGrow: artworkCollapsed ? 0 : 1,
                    flexShrink: 1,
                    flexBasis: "0%",
                    minWidth: 0,
                    overflow: "hidden",
                    transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                  }}
                  className="animate-canvas-slide-in"
                >
                  <div
                    className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                      artworkCollapsed
                        ? "translate-x-5 opacity-0"
                        : "translate-x-0 opacity-100"
                    }`}
                  >
                    {rightPane2PaneInner}
                  </div>
                </div>
              </>
            ) : null}
          </>
        )}
        <ResizeHintTooltip
          alreadySeen={Boolean(uiPrefs.resizeHintSeen)}
          onShown={() => setUiPref("resizeHintSeen", true)}
          resizableActive={useResizableLayout}
        />
      </div>
      <PasswordProtectedPdfDialog
        fileName={protectedPdfFileName}
        onClose={() => setProtectedPdfFileName(null)}
      />
    </AskPunttDropZone>
  );
}
